import { getKsdmConfig } from 'helpers/index';
// Scoped cosmetic fix: prevent the footer version-text fragment from bleeding
// past the right edge at narrow viewports (~390px). Scoped to THIS footer's own
// hashed class — `#ksdm-footer` is the shared shell id and every board renders
// one, so as a `:global` rule it leaked onto `/sizer/` and lifted its version
// line off the baseline (see the stylesheet's header comment).
import styles from './PageFooter.module.scss';

/**
 * Static page footer (#ksdm-footer) — §3.L. Copyright + software/master/CMS
 * version numbers from window.__KSDM_CONFIG__. The CMS version (C.{Ymd}.{ver})
 * is now injected by ReactAppView (versions.cms); the version block is shown
 * only when `versions.software` is present (parity with the Backbone
 * `{% if versions.software %}` guard), and the CMS segment only when present.
 *
 * `noRole` strips the column wrapper classes and applies full-width + footer-margin
 * (§ role-gating: no-role footer).
 *
 * `outerClassName`/`innerClassName` override the wrapper classes for non-
 * whiteboard shells (Phase 3 project board: `col-xs-11 col-xs-offset-1 menu-200`
 * outer / `col-xs-12 footer-wrapper bottom-3` inner per projectBase.html, with
 * the rail-collapse `.to-left` reflow applied by the caller).
 */
interface PageFooterProps {
  noRole?: boolean;
  outerClassName?: string;
  innerClassName?: string;
}

const PageFooter = ({ noRole = false, outerClassName, innerClassName }: PageFooterProps) => {
  const cfg = getKsdmConfig();
  const year = cfg?.year ?? new Date().getFullYear();
  const software = cfg?.versions?.software;
  const master = cfg?.versions?.master;
  const cmsVersion = cfg?.versions?.cms;

  const outerClass =
    outerClassName ?? (noRole ? 'full-width footer-margin' : 'col-xs-10 col-xs-offset-2');
  const innerClass =
    innerClassName ??
    (noRole ? 'footer-wrapper bottom--2' : 'col-xs-12 footer-wrapper bottom--2');

  return (
    <footer id="ksdm-footer" className={styles.pageFooter}>
      <div className={outerClass}>
        <div className={innerClass}>
          <div className="text-right">
            <small id="ksdm-copyright" className="right mt-0">
              Powered by Kinetix Air | &#169; {year} Kinetix Software LLC | All Rights Reserved
            </small>
          </div>
          {software && (
            <div className="">
              <small id="ksdm-dbs-version" className="mt-0">
                <span className="ka-has-tooltip" tooltip="Software version">
                  {software}
                </span>{' '}
                |{' '}
                <span className="ka-has-tooltip" tooltip="Master DB version">
                  {master}
                </span>
                {cmsVersion && (
                  <>
                    {' '}|{' '}
                    <span className="ka-has-tooltip cms_version" tooltip="CMS DB version">
                      {cmsVersion}
                    </span>
                  </>
                )}
              </small>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default PageFooter;
